// ── Frontend-owned navigate_ui tool ────────────────────────────────────────────
// The frontend owns the navigate_ui tool: it defines the schema, keeps the route
// registry in lockstep with the router, sends the definition to the backend with
// every chat request, and executes navigation itself. The backend only forwards
// the raw LLM call back to us — validation is our job.

export type RouteFilterType = "string" | "enum" | "date"

export interface RouteParameter {
  type: "string"
  description?: string
}

export interface RouteFilter {
  type: RouteFilterType
  enum?: string[]
  description?: string
  adminOnly?: boolean
}

export interface RouteDefinition {
  key: string // value of navigate_ui.page
  path: string // template, e.g. '/orders/:orderId'
  description: string
  parameters: Record<string, RouteParameter>
  filters: Record<string, RouteFilter>
}

/**
 * Single source of truth for navigable pages — mirrors the actual router.
 * Any change to a route (new tab, detail page, filter) must be reflected here.
 */
export const ROUTE_REGISTRY: RouteDefinition[] = [
  {
    key: "dashboard",
    path: "/dashboard",
    description: "User's main dashboard overview",
    parameters: {},
    filters: {},
  },
  {
    key: "orders",
    path: "/orders",
    description: "Purchase orders listing page (buyer, seller and admin views)",
    parameters: {},
    filters: {
      status: { type: "string", description: "Order status filter, e.g. PENDING, CONFIRMED, SHIPPED, DELIVERED, CANCELLED" },
      searchTerm: { type: "string", description: "Free-text search across order id and related names" },
      buyerId: { type: "string", description: "Filter by buyer id (seller view)" },
      companyId: { type: "string", description: "Filter by company id (buyer view)" },
      minAmount: { type: "string", description: "Minimum order amount filter" },
      maxAmount: { type: "string", description: "Maximum order amount filter" },
      startDate: { type: "date", description: "Start date filter, ISO YYYY-MM-DD" },
      endDate: { type: "date", description: "End date filter, ISO YYYY-MM-DD" },
      page: { type: "string", description: "Page number for pagination" },
    },
  },
  {
    key: "order_detail",
    path: "/orders/:orderId",
    description: "Single purchase order detailed view",
    parameters: { orderId: { type: "string", description: "The order identifier" } },
    filters: {},
  },
  {
    key: "rfqs",
    path: "/rfqs",
    description: "Requests for quotation (RFQ) listing and workspace",
    parameters: {},
    filters: {
      status: { type: "string", description: "RFQ status filter, e.g. OPEN, PENDING, CLOSED, EXPIRED" },
      searchTerm: { type: "string", description: "Free-text search across RFQ id, message or targets" },
      queryMessage: { type: "string", description: "Free-text search across RFQ messages" },
      preferredShipmentMode: { type: "string", description: "Preferred shipment mode, e.g. AIR, SEA, ROAD, RAIL" },
      shipmentType: { type: "string", description: "Shipment type filter" },
      type: { type: "string", description: "RFQ type filter, e.g. OPEN, CLOSED" },
      fromDate: { type: "date", description: "Start date filter, ISO YYYY-MM-DD" },
      toDate: { type: "date", description: "End date filter, ISO YYYY-MM-DD" },
      buyerId: { type: "string", description: "Filter by buyer id" },
      companyId: { type: "string", description: "Filter by company id" },
      page: { type: "string", description: "Page number for pagination" },
      subTab: { type: "enum", enum: ["buyers", "sellers"], description: "Admin RFQ workspace tab", adminOnly: true },
    },
  },
  {
    key: "rfq_detail",
    path: "/rfqs/:rfqId",
    description: "Single RFQ detailed view",
    parameters: { rfqId: { type: "string", description: "The RFQ identifier" } },
    filters: {
      subTab: { type: "enum", enum: ["buyers", "sellers"], description: "Admin RFQ workspace tab the detail belongs to", adminOnly: true },
    },
  },
  {
    key: "quotations",
    path: "/quotations",
    description: "Quotations listing and workspace",
    parameters: {},
    filters: {
      status: { type: "string", description: "Quotation status filter, e.g. PENDING, APPROVED, ACCEPTED, REJECTED" },
      searchTerm: { type: "string", description: "Free-text search across quotation id and details" },
      fromDate: { type: "date", description: "Start date filter, ISO YYYY-MM-DD" },
      toDate: { type: "date", description: "End date filter, ISO YYYY-MM-DD" },
      buyerId: { type: "string", description: "Filter by buyer id" },
      companyId: { type: "string", description: "Filter by company id" },
      isLatest: { type: "string", description: "Show only the latest quotation per RFQ, e.g. true" },
      page: { type: "string", description: "Page number for pagination" },
      subTab: { type: "enum", enum: ["seller", "buyer"], description: "Admin quotations workspace tab", adminOnly: true },
    },
  },
  {
    key: "quotation_detail",
    path: "/quotations/:quotationId",
    description: "Single quotation detailed view",
    parameters: { quotationId: { type: "string", description: "The quotation identifier" } },
    filters: {
      subTab: { type: "enum", enum: ["seller", "buyer"], description: "Admin quotations workspace tab the detail belongs to", adminOnly: true },
    },
  },
  {
    key: "complaints",
    path: "/complaints",
    description: "Complaints listing page",
    parameters: {},
    filters: {
      status: { type: "string", description: "Complaint status filter, e.g. OPEN, RESOLVED" },
    },
  },
  {
    key: "complaint_detail",
    path: "/complaints/:complaintId",
    description: "Single complaint detailed view",
    parameters: { complaintId: { type: "string", description: "The complaint identifier" } },
    filters: {},
  },
  {
    key: "products",
    path: "/products",
    description: "Seller's product catalogue management page",
    parameters: {},
    filters: {
      action: { type: "string", description: "Open the add-product form, e.g. add" },
    },
  },
  {
    key: "product_detail",
    path: "/products/:productId",
    description: "Seller's single product detailed view",
    parameters: { productId: { type: "string", description: "The product identifier" } },
    filters: {},
  },
  {
    key: "variant_detail",
    path: "/products/variant/:variantId",
    description: "Seller's single product variant detailed view",
    parameters: { variantId: { type: "string", description: "The product variant identifier" } },
    filters: {},
  },
  {
    key: "companies",
    path: "/companies",
    description: "Admin company verifications page",
    parameters: {},
    filters: {},
  },
  {
    key: "company_detail",
    path: "/companies/:companyId",
    description: "Single company detailed view",
    parameters: { companyId: { type: "string", description: "The company identifier" } },
    filters: {},
  },
  {
    key: "find_supplier",
    path: "/explore/suppliers",
    description: "Search for sellers and companies. Use searchTerm for free-text and companyFilters (JSON) to filter sellers.",
    parameters: {},
    filters: {
      searchTerm: { type: "string", description: "Free-text search across sellers, companies, and their products" },
      companyFilters: { type: "string", description: "JSON-encoded company/seller filter set" },
    },
  },
  {
    key: "find_products",
    path: "/explore/products",
    description: "Search for products to buy. Use searchTerm for free-text and productFilters (JSON) to filter products.",
    parameters: {},
    filters: {
      searchTerm: { type: "string", description: "Free-text search across products and their suppliers" },
      productFilters: { type: "string", description: "JSON-encoded product filter set" },
      companyFilters: { type: "string", description: "JSON-encoded company/seller filter set" },
    },
  },
  {
    key: "explore_product_detail",
    path: "/explore/products/:productId",
    description: "Buyer-facing single product detailed view (Explore hub)",
    parameters: { productId: { type: "string", description: "The product identifier" } },
    filters: {
      highlightVariantId: { type: "string", description: "Variant id to highlight on the product page" },
      companyName: { type: "string", description: "Company name for display" },
      productName: { type: "string", description: "Product name for display" },
      source: { type: "string", description: "Origin source, e.g. dashboard" },
    },
  },
  {
    key: "explore_variant_detail",
    path: "/explore/products/:productId/variants/:variantId",
    description: "Buyer-facing single product variant detailed view (Explore hub)",
    parameters: {
      productId: { type: "string", description: "The product identifier" },
      variantId: { type: "string", description: "The variant identifier" },
    },
    filters: {
      companyName: { type: "string", description: "Company name for display" },
      productName: { type: "string", description: "Product name for display" },
      variantName: { type: "string", description: "Variant name for display" },
      source: { type: "string", description: "Origin source, e.g. dashboard" },
    },
  },
  {
    key: "explore_company_detail",
    path: "/explore/companies/:companyId",
    description: "Buyer-facing single company/supplier detailed view (Explore hub)",
    parameters: { companyId: { type: "string", description: "The company identifier" } },
    filters: {
      companyType: { type: "string", description: "Company type, e.g. internal, external, lead" },
      companyName: { type: "string", description: "Company name for display" },
    },
  },
  {
    key: "browse_products",
    path: "/browse-products",
    description: "Browse products by category (Buyer dashboard)",
    parameters: {},
    filters: {
      category: { type: "string", description: "Product category to browse" },
      source: { type: "string", description: "Origin source, e.g. dashboard" },
    },
  },
  {
    key: "browse_product_detail",
    path: "/browse-products/products/:productId",
    description: "Buyer-facing single product detailed view (Browse products)",
    parameters: { productId: { type: "string", description: "The product identifier" } },
    filters: {
      highlightVariantId: { type: "string", description: "Variant id to highlight on the product page" },
      companyName: { type: "string", description: "Company name for display" },
      productName: { type: "string", description: "Product name for display" },
      source: { type: "string", description: "Origin source, e.g. dashboard, browse-products" },
      category: { type: "string", description: "Product category for back-navigation" },
    },
  },
  {
    key: "browse_variant_detail",
    path: "/browse-products/products/:productId/variants/:variantId",
    description: "Buyer-facing single product variant detailed view (Browse products)",
    parameters: {
      productId: { type: "string", description: "The product identifier" },
      variantId: { type: "string", description: "The variant identifier" },
    },
    filters: {
      companyName: { type: "string", description: "Company name for display" },
      productName: { type: "string", description: "Product name for display" },
      variantName: { type: "string", description: "Variant name for display" },
      source: { type: "string", description: "Origin source, e.g. dashboard, browse-products" },
      category: { type: "string", description: "Product category for back-navigation" },
    },
  },
  {
    key: "browse_company_detail",
    path: "/browse-supplier/companies/:companyId",
    description: "Buyer-facing single company/supplier detailed view (Browse suppliers)",
    parameters: { companyId: { type: "string", description: "The company identifier" } },
    filters: {
      companyType: { type: "string", description: "Company type, e.g. internal, external, lead" },
      companyName: { type: "string", description: "Company name for display" },
      source: { type: "string", description: "Origin source, e.g. dashboard" },
    },
  },
  {
    key: "submit_rfq",
    path: "/submit-rfq",
    description: "Form to submit a new request for quotation",
    parameters: {},
    filters: {},
  },
  {
    key: "profile",
    path: "/profile",
    description: "User account and profile page",
    parameters: {},
    filters: {
      edit: { type: "string", description: "Open the profile in edit mode, e.g. true" },
    },
  },
  {
    key: "settings",
    path: "/settings",
    description: "User account settings page",
    parameters: {},
    filters: {},
  },
  {
    key: "external_seller_detail",
    path: "/external-sellers/:leadId",
    description: "Single external seller lead detailed view",
    parameters: { leadId: { type: "string", description: "The seller lead identifier" } },
    filters: {},
  },
]

// Derive the enum from the registry so they can never drift apart.
export const NAVIGATE_UI_TOOL = {
  type: "function",
  function: {
    name: "navigate_ui",
    description:
      "Navigate the user's screen to a specific page when the conversation implies they want to see it (e.g. checking orders, viewing a profile or order detail). Only call this when a screen change is clearly useful, not for every message. For date-range requests like 'orders from March to June', resolve them into concrete startDate/endDate or fromDate/toDate filters in YYYY-MM-DD format. To look for products to buy, use page 'find_products' (or 'browse_products' to browse by category); to look for sellers/companies, use page 'find_supplier'. Use 'products' only for the seller's own catalogue management. When the user asks you to search, include their exact query in the 'searchTerm' filter — it is shown in the URL params and pre-fills the search on the target page.",
    parameters: {
      type: "object",
      properties: {
        page: {
          type: "string",
          description: "The page identifier to navigate to.",
          enum: ROUTE_REGISTRY.map((r) => r.key),
        },
        params: {
          type: "object",
          description: "Path params for detail pages (e.g. orderId). Provide them only when you know the exact identifier.",
          additionalProperties: { type: "string" },
        },
        filters: {
          type: "object",
          description: "Optional query filters for the target page.",
          additionalProperties: { type: "string" },
        },
      },
      required: ["page"],
    },
  },
} as const

export interface NavigationInput {
  page: string
  params?: Record<string, unknown>
  filters?: Record<string, unknown>
}

export type NavigationResult = { url: string } | { error: string }

export function isAdminOrSuperAdmin(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "SUPER_ADMIN"
}

export function isValidIsoDate(value: string | null | undefined): boolean {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
}

/**
 * Validate a navigation intent against the route registry and resolve it to a
 * concrete URL. Returns `{ error }` (callers must stay put — never crash) or
 * `{ url }` that can be handed straight to the router.
 */
export function validateNavigation(
  input: NavigationInput,
  userRole?: string | null
): NavigationResult {
  const route = ROUTE_REGISTRY.find((r) => r.key === input.page)
  if (!route) return { error: `Page "${input.page}" is not a valid destination.` }

  const params: Record<string, string> = {}
  for (const [key] of Object.entries(route.parameters)) {
    const raw = input.params?.[key]
    if (typeof raw !== "string" || !raw.trim()) {
      return {
        error: `Cannot open ${route.key}: path parameter "${key}" is required. Ask the user which one they mean or go to the listing page.`,
      }
    }
    params[key] = raw.trim()
  }

  const filters: Record<string, string> = {}
  for (const [key, def] of Object.entries(route.filters)) {
    // Role-aware filters (e.g. admin-only subTabs) are stripped for non-admins.
    if (def.adminOnly && !isAdminOrSuperAdmin(userRole)) continue
    const raw = input.filters?.[key]
    if (typeof raw !== "string") continue
    const candidate = raw.trim()
    if (!candidate) continue
    if (def.type === "enum" && !def.enum?.some((o) => o.toLowerCase() === candidate.toLowerCase())) continue
    if (def.type === "date" && !isValidIsoDate(candidate)) {
      return { error: `Invalid date filter "${key}": expected YYYY-MM-DD.` }
    }
    filters[key] = candidate
  }

  for (const [fromKey, toKey] of [
    ["startDate", "endDate"],
    ["fromDate", "toDate"],
    ["from", "to"],
  ] as const) {
    if (filters[fromKey] && filters[toKey] && filters[fromKey] > filters[toKey]) {
      return { error: `Invalid date range: "${fromKey}" must be on or before "${toKey}".` }
    }
  }

  return { url: buildUrl(route, params, filters) }
}

export function buildUrl(
  route: RouteDefinition,
  params: Record<string, string>,
  filters: Record<string, string>
): string {
  const path = route.path.replace(/:([A-Za-z0-9_]+)/g, (_, k: string) => params[k] ?? "")
  const qs = new URLSearchParams(filters).toString()
  return qs ? `${path}?${qs}` : path
}

// ── Frontend-owned get_page_context tool ───────────────────────────────────────
// Lets the LLM ask the frontend to report what the user is currently viewing. The
// frontend parses the browser URL against the route registry and returns the
// matched page, its path params and its active query filters. The backend
// forwards the call back to us (auto-approved, no approval gate); we compute the
// context and POST the real result back so the model can continue its turn.

export interface ParsedPageContext {
  /** Matched route path with `:param` segments removed, e.g. "browse-supplier/companies". */
  page: string
  /** navigate_ui page key, e.g. "browse_company_detail". */
  routeKey: string
  /** Path params, e.g. { companyId: "acaa34cb-..." }. */
  params: Record<string, string>
  /** Query filters (URL-decoded), e.g. { companyType: "internal", source: "dashboard" }. */
  filters: Record<string, string>
}

/**
 * Parse a URL against the route registry into the page the user is viewing.
 * Returns `null` when the URL is malformed or matches no registered route.
 */
export function parsePageUrl(rawUrl: string): ParsedPageContext | null {
  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    return null
  }

  const pathname = url.pathname.replace(/\/+$/, "") || "/"
  const segments = pathname.split("/").filter(Boolean)

  let best: { route: RouteDefinition; params: Record<string, string>; score: number } | null = null

  for (const route of ROUTE_REGISTRY) {
    const templateSegments = route.path.split("/").filter(Boolean)
    if (templateSegments.length !== segments.length) continue

    const params: Record<string, string> = {}
    let match = true
    for (let i = 0; i < templateSegments.length; i++) {
      const template = templateSegments[i]
      if (template.startsWith(":")) {
        if (!segments[i]) {
          match = false
          break
        }
        params[template.slice(1)] = decodeURIComponent(segments[i])
      } else if (template !== segments[i]) {
        match = false
        break
      }
    }
    if (!match) continue

    // When several templates share a literal prefix, prefer the one with more
    // matched path params (e.g. a detail page over a listing page).
    const score = Object.keys(params).length
    if (!best || score > best.score) best = { route, params, score }
  }

  if (!best) return null

  const filters: Record<string, string> = {}
  url.searchParams.forEach((value, key) => {
    filters[key] = value
  })

  const page = best.route.path
    .split("/")
    .filter((s) => s && !s.startsWith(":"))
    .join("/")

  return { page, routeKey: best.route.key, params: best.params, filters }
}

/** Convenience wrapper that parses the current browser URL. */
export function getCurrentPageContext(): ParsedPageContext | null {
  if (typeof window === "undefined") return null
  return parsePageUrl(window.location.href)
}

export const GET_PAGE_CONTEXT_TOOL = {
  type: "function",
  function: {
    name: "get_page_context",
    description:
      "Reports ONLY the UI page the user is currently viewing in the browser: the matched page (e.g. browse-supplier/companies), its path parameters (e.g. companyId) and the active query filters (e.g. companyType, source). Use this only when your answer depends on the specific screen the user has open, e.g. to act on or personalize advice for the current listing/detail page. This tool returns NO ENGA platform knowledge — for questions about how ENGA works, business flows, statuses, or which tool to use, call get_orchestrator_context instead. When a specific URL is known, pass it via 'url'; otherwise the user's current browser URL is used.",
    parameters: {
      type: "object",
      properties: {
        url: {
          type: "string",
          description: "Optional full URL to parse instead of the user's current browser URL.",
        },
      },
    },
  },
} as const